import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Plus, FileText, Download } from 'lucide-react';
import { AdminPOCreatorDialog } from './AdminPOCreatorDialog';

interface PurchaseOrder {
  id: string;
  purchase_order_number: string;
  status: string | null;
  created_at: string;
  campaign_id: string | null;
  pdf_file_path?: string | null;
}

export const AdminPurchaseOrderPanel = () => {
  const { language } = useLanguage();
  const [purchaseOrders, setPurchaseOrders] = useState<PurchaseOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreator, setShowCreator] = useState(false);

  const fetchPOs = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('purchase_orders')
      .select('id, purchase_order_number, status, created_at, campaign_id')
      .order('created_at', { ascending: false });

    if (!error && data) {
      setPurchaseOrders(data);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchPOs();
  }, []);

  const getStatusColor = (status: string | null) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold">
          {language === 'ar' ? 'أوامر الشراء' : 'Purchase Orders'}
        </h2>
        <Button onClick={() => setShowCreator(true)} className="gap-2">
          <Plus className="h-4 w-4" />
          {language === 'ar' ? 'إنشاء أمر شراء' : 'Create Purchase Order'}
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary" />
        </div>
      ) : purchaseOrders.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-muted-foreground">
            <FileText className="h-12 w-12 mb-4 opacity-50" />
            <p>{language === 'ar' ? 'لا توجد أوامر شراء بعد' : 'No purchase orders yet'}</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3">
          {purchaseOrders.map((po) => (
            <Card key={po.id}>
              <CardContent className="flex items-center justify-between py-4 px-6">
                <div className="flex items-center gap-4">
                  <FileText className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <p className="font-medium">{po.purchase_order_number}</p>
                    <p className="text-sm text-muted-foreground">
                      {new Date(po.created_at).toLocaleDateString()}
                      {po.campaign_id && (
                        <span className="ml-2">
                          • {language === 'ar' ? 'مرتبط بحملة' : 'Linked to campaign'}
                        </span>
                      )}
                    </p>
                  </div>
                </div>
                <Badge className={getStatusColor(po.status)}>
                  {po.status || 'draft'}
                </Badge>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <AdminPOCreatorDialog
        open={showCreator}
        onOpenChange={setShowCreator}
        onCreated={fetchPOs}
      />
    </div>
  );
};
